import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { clipLabel, clipName } from '../../lib/media';
import { SortableShell, SortableItem } from './SortableList';

const WEB_COPY = {
  ready: ['Smooth copy ready', 'bg-green-100 text-green-800 border-green-300'],
};

// Status of the background 1080p streaming copy for heavy camera files.
const WebCopyBadge = ({ video }) => {
  const meta = WEB_COPY[video.web_status];
  if (!meta) return null;
  return (
    <span className={`text-[10px] px-1.5 rounded border shrink-0 ${meta[1]}`} title={video.web_error || video.web_note || ''} data-testid={`web-copy-${video.id}`}>
      {meta[0]}
    </span>
  );
};
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Upload, Trash2, Loader2, Video, X, Film, RefreshCw, Pencil, Check, Link2, ExternalLink, Play, Copy, ThumbsUp, Share2, Youtube, Star, DollarSign, Image as ImageIcon } from 'lucide-react';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Label } from '../ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MULTIPART_THRESHOLD = 200 * 1024 * 1024;

// PUT one multipart chunk to S3, retrying network blips; resolves to its ETag.
const putPartWithRetry = async (url, blob, onProgress, tries = 4) => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded); };
        xhr.onload = () => {
          const etag = xhr.getResponseHeader('ETag');
          if (xhr.status >= 200 && xhr.status < 300 && etag) resolve(etag);
          else reject(new Error(`Part upload failed: ${xhr.status}${etag ? '' : ' (no ETag)'}`));
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(blob);
      });
    } catch (e) {
      onProgress(0);
      if (attempt >= tries) throw e;
      await new Promise((r) => setTimeout(r, 2000 * attempt));
    }
  }
};

const EventVideosTab = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEventTitle, setSelectedEventTitle] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, status: '', percent: 0 });
  const [previewFiles, setPreviewFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { fetchEvents(); }, []);

  useEffect(() => {
    fetchVideos(selectedEventId || null, selectedTag || null);
  }, [selectedEventId, selectedTag]);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  };

  const fetchVideos = async (eventId = null, tag = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/admin/event-videos`;
      const params = [];
      if (eventId) params.push(`event_id=${eventId}`);
      if (tag) params.push(`tag=${tag}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Admin-set order first (sort_order), then newest uploads.
      const sorted = (res.data.videos || []).slice().sort((a, b) => {
        const ao = a.sort_order ?? -1;
        const bo = b.sort_order ?? -1;
        if (ao !== bo) return ao - bo;
        return (b.uploaded_at || '').localeCompare(a.uploaded_at || '');
      });
      setVideos(sorted);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEventSelect = (eventId) => {
    if (eventId === 'all') {
      setSelectedEventId('');
      setSelectedEventTitle('');
    } else {
      setSelectedEventId(eventId);
      const event = events.find(e => e.id === eventId);
      setSelectedEventTitle(event?.title || '');
    }
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('video/'));
    const previews = files.map(f => ({
      file: f,
      name: f.name,
      size: (f.size / (1024 * 1024)).toFixed(1)
    }));
    setPreviewFiles(prev => [...prev, ...previews]);
  };

  const removePreview = (index) => {
    setPreviewFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Generate a thumbnail from a video file in the browser
  const generateThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      video.onloadeddata = () => {
        video.currentTime = Math.min(15, video.duration * 0.5);
      };

      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = Math.min(640, video.videoWidth);
          canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth));
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            URL.revokeObjectURL(video.src);
            resolve(blob);
          }, 'image/jpeg', 0.7);
        } catch {
          URL.revokeObjectURL(video.src);
          resolve(null);
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        resolve(null);
      };

      // Timeout fallback
      setTimeout(() => { resolve(null); }, 10000);

      video.src = URL.createObjectURL(file);
    });
  };

  // Upload a single file directly to S3 via presigned URL
  const uploadFileDirect = async (file, token) => {
    // Step 0: Generate thumbnail from video
    setUploadProgress(prev => ({ ...prev, status: `Generating thumbnail for ${file.name}...` }));
    const thumbnailBlob = await generateThumbnail(file);
    let thumbnail_url = '';

    if (thumbnailBlob) {
      try {
        const thumbRes = await axios.post(`${API_URL}/api/admin/event-videos/get-upload-url`, {
          filename: file.name.replace(/\.[^.]+$/, '_thumb.jpg'),
          event_id: selectedEventId,
          event_title: selectedEventTitle,
          content_type: 'image/jpeg'
        }, { headers: { Authorization: `Bearer ${token}` } });

        // Upload thumbnail to S3 via XHR (no auth header)
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', thumbRes.data.upload_url, true);
          xhr.setRequestHeader('Content-Type', 'image/jpeg');
          xhr.onload = () => xhr.status < 300 ? resolve() : reject();
          xhr.onerror = () => reject();
          xhr.send(thumbnailBlob);
        });
        thumbnail_url = thumbRes.data.video_url;
      } catch (e) {
        console.warn('Thumbnail upload failed, continuing without:', e);
      }
    }

    const showPct = (loaded) => {
      const pct = Math.round((loaded / file.size) * 100);
      setUploadProgress(prev => ({
        ...prev,
        status: `Uploading ${file.name}: ${pct}% (${(loaded / (1024*1024)).toFixed(1)}/${(file.size / (1024*1024)).toFixed(1)} MB)`,
        percent: pct
      }));
    };
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    let video_url;
    let filename = file.name;

    if (file.size > MULTIPART_THRESHOLD) {
      // Big files go up in 100 MB parts (S3 single uploads cap at 5 GB).
      const init = await axios.post(`${API_URL}/api/admin/event-videos/multipart/init`, {
        filename: file.name, event_id: selectedEventId, size: file.size, content_type: file.type || 'video/mp4'
      }, auth);
      const { upload_id, file_key, chunk_size, part_urls } = init.data;
      video_url = init.data.video_url;
      const loadedByPart = {};
      const etags = [];
      try {
        let next = 0;
        const worker = async () => {
          while (next < part_urls.length) {
            const { part_number, url } = part_urls[next++];
            const blob = file.slice((part_number - 1) * chunk_size, part_number * chunk_size);
            const etag = await putPartWithRetry(url, blob, (loaded) => {
              loadedByPart[part_number] = loaded;
              showPct(Object.values(loadedByPart).reduce((a, b) => a + b, 0));
            });
            etags.push({ part_number, etag });
          }
        };
        await Promise.all([worker(), worker(), worker()]);
        await axios.post(`${API_URL}/api/admin/event-videos/multipart/complete`, { file_key, upload_id, parts: etags }, auth);
      } catch (e) {
        axios.post(`${API_URL}/api/admin/event-videos/multipart/abort`, { file_key, upload_id }, auth).catch(() => {});
        throw e;
      }
    } else {
      const res = await axios.post(`${API_URL}/api/admin/event-videos/get-upload-url`, {
        filename: file.name,
        event_id: selectedEventId,
        event_title: selectedEventTitle,
        content_type: file.type || 'video/mp4'
      }, auth);
      video_url = res.data.video_url;
      filename = res.data.filename;
      // XHR, not axios: axios defaults add a Bearer header that S3 rejects.
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', res.data.upload_url, true);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) showPct(e.loaded); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`)));
        xhr.onerror = () => reject(new Error('Network error during S3 upload'));
        xhr.send(file);
      });
    }

    // Step 3: Confirm upload with backend (saves metadata to DB)
    await axios.post(`${API_URL}/api/admin/event-videos/confirm-upload`, {
      video_url,
      filename,
      event_id: selectedEventId,
      event_title: selectedEventTitle,
      size_mb: file.size / (1024 * 1024),
      thumbnail_url
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });

    return true;
  };

  const handleUpload = async () => {
    if (!selectedEventId) {
      toast.error('Please select an event first');
      return;
    }
    if (previewFiles.length === 0) {
      toast.error('No videos selected');
      return;
    }

    setUploading(true);
    setUploadError('');
    const total = previewFiles.length;
    setUploadProgress({ current: 0, total, status: 'Starting...', percent: 0 });
    let uploaded = 0;
    let lastError = '';

    const token = localStorage.getItem('token');

    for (let i = 0; i < total; i++) {
      const file = previewFiles[i];
      setUploadProgress({
        current: i,
        total,
        status: `Preparing ${file.name}...`,
        percent: 0
      });

      try {
        await uploadFileDirect(file.file, token);
        uploaded++;
        setUploadProgress({
          current: i + 1,
          total,
          status: `${file.name} uploaded!`,
          percent: 100
        });
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
        lastError = err.response?.data?.detail || err.response?.data?.message || err.message || 'Unknown error';
        setUploadProgress({
          current: i + 1,
          total,
          status: `Failed: ${file.name} - ${lastError}`,
          percent: 0
        });
      }
    }

    if (uploaded < total) {
      setUploadError(lastError);
      toast.error(`${total - uploaded} video(s) failed: ${lastError}`, { duration: 10000 });
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} video(s) uploaded!`);
      setPreviewFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    setUploading(false);
    await fetchVideos(selectedEventId || null, selectedTag || null);
  };

  const handleDeleteVideo = async (videoId) => {
    if (!confirm('Delete this video?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/event-videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(prev => prev.filter(v => v.id !== videoId));
      toast.success('Video deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const [generatingThumbs, setGeneratingThumbs] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [videoLinkTitle, setVideoLinkTitle] = useState('');
  const [videoLinkThumb, setVideoLinkThumb] = useState('');
  const [addingLink, setAddingLink] = useState(false);

  const handleAddLink = async () => {
    if (!selectedEventId) { toast.error('Please select an event first'); return; }
    if (!videoLink.trim()) { toast.error('Please paste a video URL'); return; }
    setAddingLink(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_URL}/api/admin/event-videos/add-link`, {
        video_url: videoLink.trim(),
        event_id: selectedEventId,
        event_title: selectedEventTitle,
        title: videoLinkTitle.trim(),
        thumbnail_url: videoLinkThumb.trim() || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const savedThumb = r?.data?.video?.thumbnail_url || '';
      if (savedThumb) {
        toast.success('Video link added with thumbnail');
      } else {
        toast.info('Video link added — no thumbnail found. Paste one manually if you have it (see the field below the URL).');
      }
      setVideoLink('');
      setVideoLinkTitle('');
      setVideoLinkThumb('');
      await fetchVideos(selectedEventId || null, selectedTag || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add link');
    } finally {
      setAddingLink(false);
    }
  };

  const handleRename = async (videoId) => {
    const title = editName.trim();
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/admin/event-videos/${videoId}/rename`,
        { title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVideos(prev => prev.map(v => v.id === videoId ? { ...v, title } : v));
      toast.success(title ? 'Video title saved' : 'Video title cleared');
    } catch { toast.error('Failed to save title'); }
    setEditingId(null);
  };

  const [thumbJob, setThumbJob] = useState(null);
  const thumbPollRef = React.useRef(null);

  const pollThumbJob = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/event-videos/generate-thumbnails/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThumbJob(res.data);
      if (!res.data.running) {
        // Job finished — refresh videos, stop polling, show summary
        clearInterval(thumbPollRef.current);
        thumbPollRef.current = null;
        setGeneratingThumbs(false);
        await fetchVideos(selectedEventId || null, selectedTag || null);
        const errCount = (res.data.errors || []).length;
        if (res.data.succeeded > 0) {
          toast.success(`Generated ${res.data.succeeded} thumbnail(s)${errCount ? ` · ${errCount} failed` : ''}`);
        } else if (errCount === 0 && res.data.total === 0) {
          toast.info('All videos already have thumbnails');
        } else if (errCount > 0) {
          toast.error(`${errCount} thumbnails failed`);
        }
      }
    } catch (err) {
      console.warn('Thumb job poll failed:', err);
    }
  }, [selectedEventId, selectedTag]);

  const handleGenerateThumbnails = async () => {
    setGeneratingThumbs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/admin/event-videos/generate-thumbnails`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThumbJob(res.data.state);
      // If nothing to do, res.data.state.running is false immediately
      if (!res.data.state?.running) {
        setGeneratingThumbs(false);
        toast.info('All videos already have thumbnails');
        return;
      }
      // Start polling every 2s
      if (thumbPollRef.current) clearInterval(thumbPollRef.current);
      thumbPollRef.current = setInterval(pollThumbJob, 2000);
      pollThumbJob(); // fire immediately too
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start thumbnail job');
      setGeneratingThumbs(false);
    }
  };

  // Cleanup poll interval when component unmounts
  React.useEffect(() => {
    return () => {
      if (thumbPollRef.current) clearInterval(thumbPollRef.current);
    };
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('video/'));
    const previews = files.map(f => ({
      file: f,
      name: f.name,
      size: (f.size / (1024 * 1024)).toFixed(1)
    }));
    setPreviewFiles(prev => [...prev, ...previews]);
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <Film className="w-5 h-5" />
            Event Videos
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateThumbnails}
              disabled={generatingThumbs}
              data-testid="generate-thumbnails-btn"
            >
              {generatingThumbs ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Video className="w-4 h-4 mr-1" />}
              {generatingThumbs ? 'Generating...' : 'Generate Thumbnails'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => fetchVideos(selectedEventId || null, selectedTag || null)} data-testid="refresh-videos-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Thumbnail generation progress bar */}
        {thumbJob && (thumbJob.running || (thumbJob.finished_at && (Date.now() - new Date(thumbJob.finished_at).getTime()) < 15000)) && (
          <div className="border rounded-lg p-4 bg-primary/5 border-primary/20 space-y-2" data-testid="thumb-job-progress">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 font-semibold text-sm">
                {thumbJob.running ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating thumbnails…</>
                ) : (
                  <>✓ Thumbnail generation complete</>
                )}
              </div>
              <span className="text-sm text-muted-foreground font-mono">
                {thumbJob.processed} of {thumbJob.total}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary h-full transition-all duration-500"
                style={{ width: `${thumbJob.total ? (thumbJob.processed / thumbJob.total) * 100 : 0}%` }}
              />
            </div>
            {thumbJob.current_filename && (
              <p className="text-xs text-muted-foreground truncate">
                Working on: <span className="font-mono">{thumbJob.current_filename}</span>
              </p>
            )}
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>✓ {thumbJob.succeeded} succeeded</span>
              {thumbJob.errors?.length > 0 && (
                <span className="text-amber-700">⚠ {thumbJob.errors.length} failed</span>
              )}
              {thumbJob.skipped_vimeo_youtube > 0 && (
                <span>· {thumbJob.skipped_vimeo_youtube} Vimeo/YouTube skipped (no oEmbed thumb)</span>
              )}
            </div>
            {thumbJob.errors?.length > 0 && (
              <details className="cursor-pointer">
                <summary className="text-xs text-amber-700 hover:underline">Show errors ({thumbJob.errors.length})</summary>
                <div className="mt-2 max-h-40 overflow-y-auto text-xs font-mono space-y-1 bg-background p-2 rounded border">
                  {thumbJob.errors.slice(0, 50).map((e, i) => (
                    <div key={i} className="truncate">
                      <span className="text-amber-700">•</span> {e.filename}: <span className="text-muted-foreground">{e.reason}</span>
                    </div>
                  ))}
                  {thumbJob.errors.length > 50 && (
                    <div className="text-muted-foreground italic">…and {thumbJob.errors.length - 50} more</div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Event Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Select Event</label>
          <Select value={selectedEventId || 'all'} onValueChange={handleEventSelect}>
            <SelectTrigger data-testid="event-video-select">
              <SelectValue placeholder="Choose an event..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {events.map(event => (
                <SelectItem key={event.id} value={event.id}>
                  {event.title} {event.event_date ? `(${event.event_date})` : '(TBA)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tag Filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedTag('')}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${!selectedTag ? 'bg-primary text-primary-foreground border-primary font-medium' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
            data-testid="tag-filter-all"
          >
            All Videos
          </button>
          {[
            { value: 'good', label: 'Good', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
            { value: 'social_media', label: 'Social Media', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
            { value: 'youtube', label: 'YouTube', color: 'bg-red-500/10 text-red-600 border-red-500/20' },
            { value: 'highlight', label: 'Highlight', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
          ].map(tag => (
            <button
              key={tag.value}
              onClick={() => setSelectedTag(selectedTag === tag.value ? '' : tag.value)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${selectedTag === tag.value ? tag.color + ' font-medium' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
              data-testid={`tag-filter-${tag.value}`}
            >
              {tag.label}
            </button>
          ))}
        </div>

        {/* Upload Area */}
        {selectedEventId && (
          <Tabs defaultValue="upload" className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="upload" className="flex-1 flex items-center gap-1.5" data-testid="upload-file-tab">
                <Upload className="w-4 h-4" /> Upload File
              </TabsTrigger>
              <TabsTrigger value="link" className="flex-1 flex items-center gap-1.5" data-testid="paste-link-tab">
                <Link2 className="w-4 h-4" /> Paste Video Link
              </TabsTrigger>
            </TabsList>
            <TabsContent value="upload">
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="video-drop-zone"
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Click or drag videos here</p>
                <p className="text-xs text-muted-foreground mt-1">Uploads directly to cloud storage — no size limit</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleFilesSelected}
                  className="hidden"
                />
              </div>
            </TabsContent>
            <TabsContent value="link">
              <div className="border rounded-lg p-4 space-y-3">
                <Input
                  placeholder="Paste Vimeo or YouTube URL..."
                  value={videoLink}
                  onChange={(e) => setVideoLink(e.target.value)}
                  data-testid="video-link-input"
                />
                <Input
                  placeholder="Video title (optional)"
                  value={videoLinkTitle}
                  onChange={(e) => setVideoLinkTitle(e.target.value)}
                  data-testid="video-link-title-input"
                />
                <Input
                  placeholder="Thumbnail URL (optional — paste if auto-fetch fails)"
                  value={videoLinkThumb}
                  onChange={(e) => setVideoLinkThumb(e.target.value)}
                  data-testid="video-link-thumb-input"
                />
                <p className="text-xs text-muted-foreground">
                  Auto-fetches for public Vimeo/YouTube. Restricted Vimeo videos hide their poster — paste one manually if you want a preview thumbnail.
                </p>
                <Button
                  onClick={handleAddLink}
                  disabled={addingLink || !videoLink.trim()}
                  className="w-full"
                  data-testid="add-video-link-btn"
                >
                  {addingLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Add Video Link
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Preview Files */}
        {previewFiles.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Selected ({previewFiles.length})</h4>
            {previewFiles.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between bg-muted/50 rounded p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Video className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="text-sm truncate">{file.name}</span>
                  <Badge variant="secondary" className="shrink-0">{file.size} MB</Badge>
                </div>
                {!uploading && (
                  <Button variant="ghost" size="sm" onClick={() => removePreview(idx)}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            {!uploading && (
              <Button onClick={handleUpload} className="w-full" data-testid="upload-videos-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload {previewFiles.length} Video{previewFiles.length > 1 ? 's' : ''}
              </Button>
            )}
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm font-medium">
                {uploadProgress.current} of {uploadProgress.total} done
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress.percent}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">{uploadProgress.status}</p>
          </div>
        )}

        {/* Error */}
        {uploadError && !uploading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="upload-error">
            <p className="text-sm font-medium text-red-800">Upload Error</p>
            <p className="text-sm text-red-600 mt-1">{uploadError}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setUploadError('')}>Dismiss</Button>
          </div>
        )}

        {/* Video List */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : videos.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No videos uploaded yet.</p>
        ) : (
          <VideoList
            videos={videos}
            setVideos={setVideos}
            editingId={editingId}
            setEditingId={setEditingId}
            editName={editName}
            setEditName={setEditName}
            handleRename={handleRename}
            handleDeleteVideo={handleDeleteVideo}
            fetchVideos={() => fetchVideos(selectedEventId || null, selectedTag || null)}
            reorderEventId={selectedEventId && !selectedTag ? selectedEventId : null}
          />
        )}
      </CardContent>
    </Card>
  );
};

const TAG_OPTIONS = [
  { value: 'good', label: 'Good', icon: ThumbsUp, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  { value: 'social_media', label: 'Social Media', icon: Share2, color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  { value: 'youtube', label: 'YouTube', icon: Youtube, color: 'bg-red-500/10 text-red-600 border-red-500/20' },
  { value: 'highlight', label: 'Highlight', icon: Star, color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
];

const VideoList = ({ videos, setVideos, editingId, setEditingId, editName, setEditName, handleRename, handleDeleteVideo, fetchVideos, reorderEventId }) => {
  const canReorder = !!reorderEventId && videos.length > 1;
  const saveOrder = async (next) => {
    const prev = videos;
    setVideos(next);
    try {
      await axios.put(`${API_URL}/api/admin/event-videos/reorder`,
        { event_id: reorderEventId, ids: next.map((v) => v.id) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Order saved');
    } catch {
      setVideos(prev);
      toast.error('Could not save the new order');
    }
  };
  const [playingVideo, setPlayingVideo] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const [teasingId, setTeasingId] = useState(null);
  // Thumbnail-editor dialog state
  const [thumbEditVideo, setThumbEditVideo] = useState(null);
  const [thumbEditUrl, setThumbEditUrl] = useState('');
  const [thumbEditSaving, setThumbEditSaving] = useState(false);

  const toggleTag = async (videoId, tag) => {
    const video = videos.find(v => v.id === videoId);
    if (!video) return;
    const currentTags = video.tags || [];
    const newTags = currentTags.includes(tag) ? currentTags.filter(t => t !== tag) : [...currentTags, tag];
    try {
      await axios.patch(`${API_URL}/api/admin/event-videos/${videoId}/tags`, { tags: newTags });
      setVideos(prev => prev.map(v => v.id === videoId ? { ...v, tags: newTags } : v));
    } catch { toast.error('Failed to update tags'); }
  };

  const toggleForSale = async (video) => {
    const next = !video.for_sale;
    try {
      await axios.patch(`${API_URL}/api/admin/event-videos/${video.id}/for-sale`, { for_sale: next });
      setVideos(prev => prev.map(v => v.id === video.id ? { ...v, for_sale: next } : v));
      toast.success(next ? 'Now listed for sale in the Gallery' : 'Removed from the Gallery — no longer for sale');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const setThumbnail = (video) => {
    setThumbEditVideo(video);
    setThumbEditUrl(video.thumbnail_url || '');
  };
  const closeThumbEdit = () => {
    setThumbEditVideo(null);
    setThumbEditUrl('');
    setThumbEditSaving(false);
  };
  const saveThumbEdit = async () => {
    if (!thumbEditVideo) return;
    setThumbEditSaving(true);
    try {
      const r = await axios.patch(`${API_URL}/api/admin/event-videos/${thumbEditVideo.id}/thumbnail`, { thumbnail_url: thumbEditUrl.trim() });
      const newThumb = r.data?.thumbnail_url || '';
      setVideos(prev => prev.map(v => v.id === thumbEditVideo.id ? { ...v, thumbnail_url: newThumb } : v));
      toast.success(newThumb ? 'Thumbnail saved' : 'Cleared — auto-fetch found nothing');
      closeThumbEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setThumbEditSaving(false);
    }
  };

  const copyToLibrary = async (video) => {
    setCopyingId(video.id);
    try {
      await axios.post(`${API_URL}/api/admin/event-videos/${video.id}/copy-to-library`, {
        title: clipLabel(video),
        category: 'heading',
      });
      toast.success('Video copied to library!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to copy');
    }
    setCopyingId(null);
  };

  const copyToTeaser = async (video, type = null) => {
    // If no type chosen, check both and pick one with space, or ask
    if (!type) {
      const [tRes, lRes] = await Promise.all([
        axios.get(`${API_URL}/api/teaser-videos`),
        axios.get(`${API_URL}/api/live-event-teasers`)
      ]);
      const teaserCount = (tRes.data.videos || []).length;
      const liveCount = (lRes.data.videos || []).length;
      const teaserFull = teaserCount >= 6;
      const liveFull = liveCount >= 6;

      if (teaserFull && liveFull) {
        toast.error('All 12 slots are full (6 teasers + 6 live events). Remove one in Admin > Teasers first.');
        return;
      }

      if (!teaserFull && !liveFull) {
        // Ask which one
        const choice = window.confirm('Add to Teaser Videos (vertical)?\n\nClick OK for Teaser Videos\nClick Cancel for Live Event Highlights');
        type = choice ? 'teaser' : 'live';
      } else {
        type = teaserFull ? 'live' : 'teaser';
      }
    }

    setTeasingId(video.id);
    try {
      const endpoint = type === 'live' ? 'live-event-teasers' : 'teaser-videos';
      const adminEndpoint = type === 'live' ? 'admin/live-event-teasers' : 'admin/teaser-videos';
      const label = type === 'live' ? 'Live Event Highlights' : 'Teaser Videos';

      const res = await axios.get(`${API_URL}/api/${endpoint}`);
      const current = res.data.videos || [];
      if (current.length >= 6) {
        toast.error(`All 6 ${label} slots are full. Remove one in Admin > Teasers first.`);
        setTeasingId(null);
        return;
      }
      const updated = [...current, { url: video.url, title: clipLabel(video) }];
      await axios.put(`${API_URL}/api/${adminEndpoint}`, { videos: updated });
      toast.success(`Added to ${label} slot ${updated.length}!`);
    } catch {
      toast.error('Failed to add to teasers');
    }
    setTeasingId(null);
  };

  const getVideoUrl = (url) => url?.startsWith('/uploads') ? `${API_URL}${url}` : url;
  const isExternal = (v) => {
    const url = v.url || '';
    return url.includes('youtube') || url.includes('youtu.be') || url.includes('vimeo') || v.video_type === 'external';
  };
  const getEmbedUrl = (url) => {
    if (url.includes('youtube.com/watch')) return `https://www.youtube.com/embed/${url.match(/v=([^&]+)/)?.[1] || ''}?autoplay=1`;
    if (url.includes('youtu.be')) return `https://www.youtube.com/embed/${url.split('/').pop()?.split('?')[0]}?autoplay=1`;
    if (url.includes('vimeo.com')) return `https://player.vimeo.com/video/${url.match(/vimeo\.com\/(\d+)/)?.[1] || ''}?autoplay=1`;
    return url;
  };

  return (
    <>
      <div className="space-y-3">
        <h4 className="text-sm font-medium">
          {videos.length} Video{videos.length > 1 ? 's' : ''}
          {canReorder && <span className="ml-2 text-xs font-normal text-muted-foreground" data-testid="reorder-hint">Drag the handle on the left to reorder — viewers see this order</span>}
        </h4>
        <SortableShell items={videos} enabled={canReorder} onReorder={saveOrder}>
        {videos.map(video => (
          <SortableItem key={video.id} id={video.id} enabled={canReorder}>
          <div key={video.id} className="border rounded-lg p-3 space-y-2" data-testid={`video-item-${video.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* Play button */}
                <button
                  onClick={() => setPlayingVideo(video)}
                  className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
                  title="Play video"
                  data-testid={`play-video-${video.id}`}
                >
                  <Play className="w-5 h-5 text-primary ml-0.5" />
                </button>
                <div className="min-w-0 flex-1">
                  {editingId === video.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground whitespace-nowrap max-w-[40%] truncate" title="Event name stays as-is">{video.event_title} —</span>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRename(video.id); if (e.key === 'Escape') setEditingId(null); }}
                        placeholder="Video title (e.g. Round 2 Heelers)"
                        className="h-7 text-sm"
                        autoFocus
                        data-testid={`video-title-input-${video.id}`}
                      />
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleRename(video.id)} data-testid={`video-title-save-${video.id}`}><Check className="w-4 h-4 text-green-600" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <p className="text-sm font-medium truncate cursor-pointer hover:text-primary group/name flex items-center gap-1" onClick={() => { setEditingId(video.id); setEditName(video.title || clipName(video)); }} data-testid={`video-title-${video.id}`}>
                      {clipLabel(video)}
                      {!video.title && <span className="text-[10px] px-1.5 rounded bg-amber-100 text-amber-800 border border-amber-300 shrink-0">add title</span>}
                      <WebCopyBadge video={video} />
                      <Pencil className="w-3 h-3 opacity-50 group-hover/name:opacity-100 shrink-0" />
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    File: {video.filename} {video.size_mb ? `· ${video.size_mb} MB` : ''} · {new Date(video.uploaded_at).toLocaleDateString()}
                    {video.video_type === 'external' && <Badge variant="outline" className="ml-2 text-[10px] py-0">External</Badge>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <label
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs cursor-pointer select-none ${video.for_sale ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-300' : 'bg-muted/40 border-border text-muted-foreground'}`}
                  title={video.for_sale ? 'Listed for sale in the public Gallery — click to hide' : 'NOT listed for sale — click to enable'}
                  data-testid={`for-sale-label-${video.id}`}
                >
                  <DollarSign className="w-3 h-3" />
                  <span className="hidden sm:inline">{video.for_sale ? 'For Sale' : 'Not for Sale'}</span>
                  <Switch
                    checked={!!video.for_sale}
                    onCheckedChange={() => toggleForSale(video)}
                    data-testid={`for-sale-switch-${video.id}`}
                  />
                </label>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setThumbnail(video)}
                  title={video.thumbnail_url ? 'Change thumbnail' : 'No thumbnail — click to add one'}
                  data-testid={`set-thumb-${video.id}`}
                  className={video.thumbnail_url ? '' : 'border-amber-500 text-amber-600 hover:bg-amber-50'}
                >
                  <ImageIcon className="w-4 h-4 mr-1" />
                  {video.thumbnail_url ? 'Thumb' : 'Add thumb'}
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => copyToTeaser(video)}
                  disabled={teasingId === video.id}
                  title="Copy to Teaser"
                  data-testid={`copy-to-teaser-${video.id}`}
                >
                  {teasingId === video.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => copyToLibrary(video)}
                  disabled={copyingId === video.id}
                  title="Copy to Library"
                  data-testid={`copy-to-library-${video.id}`}
                >
                  {copyingId === video.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleDeleteVideo(video.id)} data-testid={`delete-video-${video.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 pl-[52px]">
              {TAG_OPTIONS.map(tag => {
                const active = (video.tags || []).includes(tag.value);
                const Icon = tag.icon;
                return (
                  <button
                    key={tag.value}
                    onClick={() => toggleTag(video.id, tag.value)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-all ${
                      active ? tag.color + ' font-medium' : 'bg-background text-muted-foreground border-border hover:bg-muted opacity-60'
                    }`}
                    data-testid={`tag-${tag.value}-${video.id}`}
                  >
                    <Icon className="w-3 h-3" />
                    {tag.label}
                  </button>
                );
              })}
            </div>
          </div>
          </SortableItem>
        ))}
        </SortableShell>
      </div>

      {/* Video Player Modal */}
      <Dialog open={!!playingVideo} onOpenChange={() => setPlayingVideo(null)}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="font-display text-lg uppercase truncate">{playingVideo?.filename}</DialogTitle>
          </DialogHeader>
          {playingVideo && (
            <div className="aspect-video bg-black">
              {isExternal(playingVideo) ? (
                <iframe
                  src={getEmbedUrl(playingVideo.url)}
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share; clipboard-write"
                  allowFullScreen
                  frameBorder="0"
                />
              ) : (
                <video
                  src={getVideoUrl(playingVideo.url)}
                  controls
                  autoPlay
                  className="w-full h-full"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Thumbnail editor */}
      <Dialog open={!!thumbEditVideo} onOpenChange={(o) => !o && closeThumbEdit()}>
        <DialogContent className="max-w-lg" data-testid="thumb-edit-dialog">
          <DialogHeader>
            <DialogTitle>Set thumbnail</DialogTitle>
            <DialogDescription className="text-xs truncate">{thumbEditVideo?.filename}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {thumbEditUrl ? (
              <img src={thumbEditUrl} alt="preview" className="w-full aspect-video object-cover rounded border" />
            ) : (
              <div className="w-full aspect-video rounded border bg-muted flex items-center justify-center text-muted-foreground text-sm">
                No thumbnail yet
              </div>
            )}
            <Input
              placeholder="Paste image URL"
              value={thumbEditUrl}
              onChange={(e) => setThumbEditUrl(e.target.value)}
              data-testid="thumb-edit-url"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              For public Vimeo/YouTube videos leave the URL blank and press Save — the server will try to auto-fetch. For restricted Vimeo videos, paste an image URL.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeThumbEdit} disabled={thumbEditSaving}>Cancel</Button>
            <Button onClick={saveThumbEdit} disabled={thumbEditSaving} data-testid="thumb-edit-save">
              {thumbEditSaving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EventVideosTab;
